import { React, useState, useEffect } from "react";
import PageLoader from "../loader/PageLoader";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { Box, Grid, Paper } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardActionArea from "@mui/material/CardActionArea";
import {
  BarChart,
  Bar,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const Home = () => {
  const [isFetchingDashboardData, setIsFetchingDashboardData] = useState(true);
  const [refreshDashboardData, setRefreshDashboardData] = useState(null);

  // Fetch dashboard data
  const [dailyGrossMonthRevenue, setDailyGrossMonthRevenue] = useState([]);
  const [grossRevenueByPeriod, setGrossRevenueByPeriod] = useState(0);
  const [commissionByPeriod, setCommissionByPeriod] = useState(0);
  const [expenseByPeriod, setExpenseByPeriod] = useState(0);
  const [netRevenueByPeriod, setNetRevenueByPeriod] = useState(0);
  const [totalWorkers, setTotalWorkers] = useState(0);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalBranches, setTotalBranches] = useState(0);
  const [totalSpecialities, setTotalSpecialities] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);

  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const lastPaperStyle = {
    padding: 20,
    height: "45vh",
    width: 1220,
    margin: "20px auto",
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  useEffect(() => {
    if (!isFetchingDashboardData) {
      setIsFetchingDashboardData(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getMonthlyRevenueBarChartData = async () => {
      try {
        const DASHBOARD_URL = "api/v1/tenant-dashboard?tenantId=";
        const response = await axiosPrivate.get(
          `${DASHBOARD_URL}${auth.tenantId}&startDate=null&endDate=null`,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        //const cUser = JSON.stringify(response.data.data, null, 4);

        isMounted &&
          setDailyGrossMonthRevenue(response.data.data.dailyGrossMonthRevenue);
        isMounted &&
          setGrossRevenueByPeriod(response.data.data.grossRevenueByPeriod);
        isMounted &&
          setCommissionByPeriod(response.data.data.commissionByPeriod);
        isMounted && setExpenseByPeriod(response.data.data.expenseByPeriod);
        isMounted &&
          setNetRevenueByPeriod(response.data.data.netRevenueByPeriod);
        isMounted && setTotalWorkers(response.data.data.totalWorkers);
        isMounted && setTotalAdmins(response.data.data.totalAdmins);
        isMounted && setTotalCustomers(response.data.data.totalCustomers);
        isMounted && setTotalBranches(response.data.data.totalBranches);
        isMounted && setTotalSpecialities(response.data.data.totalSpecialities);
        isMounted && setTotalJobs(response.data.data.totalJobs);

        setIsFetchingDashboardData(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingDashboardData(false);
      }
    };

    getMonthlyRevenueBarChartData();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshDashboardData]);

  return isFetchingDashboardData ? (
    <PageLoader title="Loading dashboard..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {dailyGrossMonthRevenue?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <Grid>
            <Box height={30} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Net Revenue
                        </Typography>
                        <Typography variant="h5" component="div">
                          Ksh.{netRevenueByPeriod.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Gross Revenue
                        </Typography>
                        <Typography variant="h6" component="div">
                          Ksh.{grossRevenueByPeriod.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Worker Commission
                        </Typography>
                        <Typography variant="h6" component="div">
                          Ksh.{commissionByPeriod.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Expenses
                        </Typography>
                        <Typography variant="h6" component="div">
                          Ksh.{expenseByPeriod.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={3}
              sx={{
                mt: 0.1,
              }}
            >
              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Customers
                        </Typography>
                        <Typography variant="h5" component="div">
                          {totalCustomers}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lifetime
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Jobs
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalJobs}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lifetime
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Specialities
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalSpecialities}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lifetime
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Branches
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalBranches}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lifetime
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Paper elevation={10} style={lastPaperStyle}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      width={500}
                      height={300}
                      data={dailyGrossMonthRevenue}
                      stackOffset="sign"
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <ReferenceLine y={0} stroke="#000" />
                      <Bar dataKey="revenue" fill="#8884d8" stackId="stack" />
                      <Bar
                        dataKey="commission"
                        fill="#82ca9d"
                        stackId="stack"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No dashboard data to display
          </Typography>
        </div>
      )}
    </Box>
  );
};

export default Home;
